import React, { useState } from 'react'

import FeatureNotEnabled from '../../components/FeatureNotEnabled'
import { Card } from '../../components/UI'
import {
    Accessibility, CheckCircle2, XCircle, AlertTriangle,
    Eye, Type, Contrast, MousePointer, Activity, Info
} from 'lucide-react'

type TabType = 'scan' | 'checklist' | 'docs'
type Severity = 'pass' | 'warning' | 'fail'

interface A11yRule {
    id: string
    label: string
    desc: string
    category: string
    severity: Severity
    wcag: string
}

const RULES: A11yRule[] = [
    { id: 'a11y-01', label: 'Alt text en imágenes', desc: 'Todas las imágenes tienen atributo alt descriptivo', category: 'Contenido', severity: 'pass', wcag: '1.1.1' },
    { id: 'a11y-02', label: 'Contraste de color', desc: 'Ratio mínimo 4.5:1 para texto normal, 3:1 para grande', category: 'Visual', severity: 'pass', wcag: '1.4.3' },
    { id: 'a11y-03', label: 'Navegación por teclado', desc: 'Todos los elementos interactivos accesibles con Tab', category: 'Interacción', severity: 'warning', wcag: '2.1.1' },
    { id: 'a11y-04', label: 'Etiquetas de formulario', desc: 'Inputs tienen labels asociados o aria-label', category: 'Formularios', severity: 'pass', wcag: '1.3.1' },
    { id: 'a11y-05', label: 'Jerarquía de headings', desc: 'h1→h2→h3 sin saltar niveles', category: 'Estructura', severity: 'pass', wcag: '1.3.1' },
    { id: 'a11y-06', label: 'Focus visible', desc: 'Indicador visual de foco en elementos activos', category: 'Visual', severity: 'warning', wcag: '2.4.7' },
    { id: 'a11y-07', label: 'Aria roles', desc: 'Landmarks y roles ARIA correctamente aplicados', category: 'Semántica', severity: 'pass', wcag: '4.1.2' },
    { id: 'a11y-08', label: 'Responsive zoom 200%', desc: 'Contenido legible y funcional al 200% de zoom', category: 'Visual', severity: 'pass', wcag: '1.4.4' },
    { id: 'a11y-09', label: 'Skip navigation', desc: 'Link para saltar al contenido principal', category: 'Navegación', severity: 'fail', wcag: '2.4.1' },
    { id: 'a11y-10', label: 'Animaciones reducidas', desc: 'Respetar prefers-reduced-motion', category: 'Visual', severity: 'warning', wcag: '2.3.3' },
]

export default function AccessibilityPage() {
    const [tab, setTab] = useState<TabType>('scan')

    const passCount = RULES.filter(r => r.severity === 'pass').length
    const warnCount = RULES.filter(r => r.severity === 'warning').length
    const failCount = RULES.filter(r => r.severity === 'fail').length
    const score = Math.round((passCount / RULES.length) * 100)

    const sevStyles: Record<Severity, { icon: React.ReactNode; color: string; bg: string; border: string }> = {
        pass: { icon: <CheckCircle2 size={16} />, color: 'text-neon-green', bg: 'bg-neon-green/10', border: 'border-neon-green/20' },
        warning: { icon: <AlertTriangle size={16} />, color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
        fail: { icon: <XCircle size={16} />, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
    }

    const tabs = [
        { id: 'scan' as const, label: 'Scan Results', icon: <Activity size={16} /> },
        { id: 'checklist' as const, label: 'WCAG Checklist', icon: <CheckCircle2 size={16} /> },
        { id: 'docs' as const, label: 'Documentación', icon: <Info size={16} /> },
    ]

    return (
        <div className="p-6 max-w-5xl mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                    <Accessibility className="text-neon-blue" /> Accesibilidad (A11y)
                </h1>
                <p className="text-gray-500 text-sm">Scanner WCAG 2.1 — Nivel AA</p>
            </div>

            <FeatureNotEnabled moduleName="Accesibilidad (A11y)" />

            {/* Score + Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
                <Card className="md:col-span-1">
                    <div className="text-center py-2">
                        <div className="relative w-20 h-20 mx-auto mb-2">
                            <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                                <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="3" />
                                <circle cx="18" cy="18" r="15.5" fill="none" stroke={score >= 80 ? '#00ff88' : score >= 50 ? '#eab308' : '#ef4444'} strokeWidth="3" strokeDasharray={`${score * 0.975} 100`} strokeLinecap="round" className="transition-all duration-700" />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className={`text-lg font-bold font-mono ${score >= 80 ? 'text-neon-green' : score >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>{score}%</span>
                            </div>
                        </div>
                        <span className="text-[10px] text-gray-500 uppercase tracking-wider">A11y Score</span>
                    </div>
                </Card>
                {[
                    { label: 'Pasados', value: passCount, color: 'text-neon-green', icon: <CheckCircle2 size={16} /> },
                    { label: 'Advertencias', value: warnCount, color: 'text-yellow-400', icon: <AlertTriangle size={16} /> },
                    { label: 'Fallos', value: failCount, color: 'text-red-400', icon: <XCircle size={16} /> },
                ].map((s, i) => (
                    <Card key={i} className="!p-4 text-center">
                        <div className={`${s.color} mb-2 flex justify-center`}>{s.icon}</div>
                        <div className="text-2xl font-bold font-mono text-white">{s.value}</div>
                        <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-1">{s.label}</div>
                    </Card>
                ))}
            </div>

            <div className="flex gap-1 mb-6 bg-charcoal rounded-lg p-1 border border-white/5">
                {tabs.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20' : 'text-gray-500 hover:text-white'}`}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {tab === 'scan' && (
                <div className="space-y-2">
                    {RULES.map(rule => {
                        const s = sevStyles[rule.severity]
                        return (
                            <Card key={rule.id} className="!p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={s.color}>{s.icon}</div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-white font-medium text-sm">{rule.label}</span>
                                                <code className="text-[10px] text-gray-600 bg-white/5 px-1.5 py-0.5 rounded font-mono">WCAG {rule.wcag}</code>
                                            </div>
                                            <p className="text-gray-500 text-xs mt-0.5">{rule.desc}</p>
                                        </div>
                                    </div>
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${s.bg} ${s.color} border ${s.border}`}>
                                        {rule.severity.toUpperCase()}
                                    </span>
                                </div>
                            </Card>
                        )
                    })}
                </div>
            )}

            {tab === 'checklist' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-4">WCAG 2.1 — Principios</h2>
                    <div className="space-y-4">
                        {[
                            { p: '1. Perceptible', items: ['Alt text', 'Contraste', 'Resize 200%', 'Captions'] },
                            { p: '2. Operable', items: ['Teclado', 'Skip nav', 'Focus visible', 'Tiempo suficiente'] },
                            { p: '3. Comprensible', items: ['Idioma', 'Labels', 'Errores descriptivos', 'Consistencia'] },
                            { p: '4. Robusto', items: ['HTML válido', 'ARIA correcto', 'Name/Role/Value', 'Compatible AT'] },
                        ].map(section => (
                            <div key={section.p}>
                                <h3 className="text-neon-blue text-xs font-bold mb-2">{section.p}</h3>
                                <div className="grid grid-cols-2 gap-1">
                                    {section.items.map(item => (
                                        <div key={item} className="flex items-center gap-2 text-sm text-gray-400 p-1.5">
                                            <CheckCircle2 size={12} className="text-neon-green shrink-0" />
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {tab === 'docs' && (
                <div className="space-y-4">
                    <Card>
                        <h2 className="text-white font-bold text-sm mb-3">Herramientas Recomendadas</h2>
                        <div className="space-y-2">
                            {[
                                { tool: 'axe-core', desc: 'Motor de testing A11y — integrable con Cypress/Playwright' },
                                { tool: 'Lighthouse', desc: 'Auditoría automática de Chrome DevTools' },
                                { tool: 'WAVE', desc: 'Extensión browser para evaluación visual' },
                                { tool: 'Pa11y', desc: 'CLI para testing automatizado en CI/CD' },
                            ].map(t => (
                                <div key={t.tool} className="p-3 bg-black/20 rounded-lg flex items-center gap-3">
                                    <code className="text-neon-blue text-xs font-mono bg-neon-blue/5 px-2 py-0.5 rounded">{t.tool}</code>
                                    <span className="text-gray-400 text-xs">{t.desc}</span>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>
            )}
        </div>
    )
}
